import {isClasswiseInstance} from 'common/HelperFunctions'
import Button from 'common/components/Button'
import {
  CREDENTIAL_REASON_KEYS,
  CredentialReason,
  CredentialsSummary,
  EMAIL_PROBLEM_REASONS,
  downloadCredentialsCsv,
  normalizeCredentialsSummary,
} from 'common/helpers/passwordProvisioning'
import React, {useEffect, useState} from 'react'
import {Modal, Nav, Pagination, Tab, Table} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {Link, useLocation, useNavigate} from 'react-router'
import {getTimetableListData} from 'setup/redux/Actions/Timetable/timetable'

import {KTIcon} from '_metronic/helpers'

interface ImportResult {
  created: {
    [tableName: string]: any[]
  }
  updated: {
    [tableName: string]: any[]
  }
  errors?: Array<{
    row: number
    data: any
    error: string
  }>
  /** Random password provisioning: every generated password + its email outcome. */
  credentials?: CredentialsSummary | null
}

interface PaginatedData {
  data: any[]
  totalCount: number
  currentPage: number
}

interface TabData {
  [key: string]: PaginatedData
}

interface ImportResultModalProps {
  show: boolean
  onHide: () => void
  result: ImportResult | null
  onClose?: () => void
  /** Done normally exits to the dashboard. Hosts embedding this modal in another flow
   *  (the onboarding wizard) pass true so Done only closes and the user stays in place. */
  disableDoneNavigation?: boolean
}

const STATS_LEVEL_ID = 0

const STATS_CONFIG = [
  {
    key: 'student_count' as const,
    labelId: 'SCHEDULE.TIMETABLE.STUDENTS',
    linkTo: '/institute/student',
  },
  {
    key: 'teacher_count' as const,
    labelId: 'SCHEDULE.TIMETABLE.TEACHERS',
    linkTo: '/institute/teacher',
  },
  {
    key: 'subject_count' as const,
    labelId: 'SCHEDULE.TIMETABLE.SUBJECTS',
    linkTo: '/institute/subjects',
  },
  {
    key: 'class_count' as const,
    labelId: 'SCHEDULE.TIMETABLE.CLASSES',
    linkTo: '/institute/classes',
  },
  {
    key: 'classroom_count' as const,
    labelId: 'SCHEDULE.TIMETABLE.VENUES',
    linkTo: '/institute/schedule/daily/class',
  },
]

/**
 * Generated passwords from a bulk import (random password provisioning): the
 * email outcome, a warning for addresses that could not be used, a one-time
 * CSV download, and the username/password table. Shown inline so it survives
 * the tab switching of the surrounding modal.
 */
const ImportCredentials: React.FC<{summary: CredentialsSummary}> = ({summary}) => {
  const intl = useIntl()
  const reasonLabel = (reason: CredentialReason | null) =>
    reason ? intl.formatMessage({id: CREDENTIAL_REASON_KEYS[reason]}) : ''
  const problems = summary.entries.filter(
    (e) => !e.emailed && e.reason && EMAIL_PROBLEM_REASONS.includes(e.reason)
  )
  return (
    <div className='border rounded p-5 mb-6' data-testid='import-credentials'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <div>
          <span className='fw-bold fs-5 text-gray-900 d-block'>
            {intl.formatMessage({id: 'CSV_IMPORT.RESULT.CREDENTIALS_TITLE'})}
          </span>
          <span className='text-muted fs-7'>
            {summary.emailRequested
              ? intl.formatMessage(
                  {id: 'USER.REGENERATED_PASSWORDS_EMAILED'},
                  {emailed: summary.emailed, total: summary.total}
                )
              : intl.formatMessage({id: 'USER.CREDENTIALS_EMAIL_OFF'})}
          </span>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() =>
            downloadCredentialsCsv(summary.entries, 'import-login-credentials.csv', reasonLabel)
          }
          data-testid='credentials-download-csv'
        >
          <KTIcon iconName='file-down' className='fs-4' />
          {intl.formatMessage({id: 'USER.CREDENTIALS_DOWNLOAD_CSV'})}
        </button>
      </div>
      {summary.emailRequested && problems.length > 0 && (
        <div className='alert alert-warning d-flex align-items-start mb-4'>
          <KTIcon iconName='information' className='fs-2x text-warning me-3' />
          <div>
            <div className='fw-bold'>
              {intl.formatMessage(
                {id: 'USER.CREDENTIALS_EMAIL_PROBLEMS'},
                {count: problems.length}
              )}
            </div>
            <div className='fs-7'>
              {problems.map((e) => `${e.username}: ${reasonLabel(e.reason)}`).join(' · ')}
            </div>
          </div>
        </div>
      )}
      <div className='table-responsive' style={{maxHeight: 260}}>
        <table className='table table-row-dashed table-sm align-middle gy-2 mb-0'>
          <thead>
            <tr className='fw-bold text-muted'>
              <th>{intl.formatMessage({id: 'USER.USERNAME'})}</th>
              <th>{intl.formatMessage({id: 'USER.PASSWORD'})}</th>
              <th>{intl.formatMessage({id: 'USER.EMAIL'})}</th>
            </tr>
          </thead>
          <tbody>
            {summary.entries.map((e) => (
              <tr key={e.userId}>
                <td>{e.username}</td>
                <td>
                  <code className='user-select-all'>{e.password}</code>
                </td>
                <td className='fs-7'>
                  {e.emailed ? (
                    <span className='badge badge-light-success'>
                      {intl.formatMessage({id: 'USER.CREDENTIAL_EMAILED'})}
                    </span>
                  ) : (
                    <span className='text-muted'>{e.email ?? '—'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const ImportResultModal: React.FC<ImportResultModalProps> = ({
  show,
  onHide,
  result,
  onClose,
  disableDoneNavigation = false,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const handleClose = () => {
    onHide()
    if (onClose) {
      onClose()
    }
  }

  const handleDone = () => {
    handleClose()
    if (!disableDoneNavigation) {
      navigate('/dashboard')
    }
  }
  const [activeTab, setActiveTab] = useState('summary')
  const [tabData, setTabData] = useState<TabData>({})
  const [loadingTabs, setLoadingTabs] = useState<Set<string>>(new Set())
  const [timetableStats, setTimetableStats] = useState<{
    student_count: number
    teacher_count: number
    subject_count: number
    class_count: number
    classroom_count: number
  } | null>(null)
  const [loadingTimetableStats, setLoadingTimetableStats] = useState(false)
  const isClasswise = isClasswiseInstance()

  const ITEMS_PER_PAGE = 8

  useEffect(() => {
    // Reset tab data when modal opens with new result
    if (show && result) {
      setTabData({})
      setActiveTab('summary')
    }
  }, [show, result])

  // Fetch list-data (timetable stats) when modal opens to show empty-stats warning if any
  useEffect(() => {
    if (!show || !result) {
      return
    }
    setLoadingTimetableStats(true)
    const onSuccess = (res: any) => {
      setLoadingTimetableStats(false)
      if (res?.result && res.result.length > 0) {
        setTimetableStats(res.result[0])
      } else {
        setTimetableStats(null)
      }
    }
    const onFailure = () => {
      setLoadingTimetableStats(false)
      setTimetableStats(null)
    }
    dispatch(getTimetableListData(STATS_LEVEL_ID, onSuccess, onFailure))
  }, [show, result, dispatch])

  if (!result) return null

  const getTotalCount = (records: {[key: string]: any[]}) => {
    return Object.values(records).reduce(
      (total, tableRecords) => total + removeDuplicates(tableRecords).length,
      0
    )
  }

  const removeDuplicates = (records: any[]) => {
    const seen = new Set()
    return records.filter((record) => {
      const key = JSON.stringify(record)
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
  }

  const removeDuplicateErrors = (errors: Array<{row: number; data: any; error: string}>) => {
    const seen = new Set()
    return errors.filter((errorItem) => {
      const key = errorItem.error
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
  }

  const loadTabData = (tabKey: string, page: number = 1) => {
    setLoadingTabs((prev) => new Set([...prev, tabKey]))

    // Simulate API call delay for demo - in real implementation, this would be an API call
    setTimeout(() => {
      let sourceData: any[] = []

      if (tabKey.startsWith('created-')) {
        const tableName = tabKey.replace('created-', '')
        sourceData = removeDuplicates(result.created[tableName] || [])
      } else if (tabKey.startsWith('updated-')) {
        const tableName = tabKey.replace('updated-', '')
        sourceData = removeDuplicates(result.updated[tableName] || [])
      } else if (tabKey === 'errors') {
        sourceData = removeDuplicateErrors(result.errors || [])
      }

      const startIndex = (page - 1) * ITEMS_PER_PAGE
      const endIndex = startIndex + ITEMS_PER_PAGE
      const paginatedData = sourceData.slice(startIndex, endIndex)

      setTabData((prev) => ({
        ...prev,
        [tabKey]: {
          data: paginatedData,
          totalCount: sourceData.length,
          currentPage: page,
        },
      }))

      setLoadingTabs((prev) => {
        const newSet = new Set(prev)
        newSet.delete(tabKey)
        return newSet
      })
    }, 300) // Simulate loading time
  }

  const handleTabSelect = (tabKey: string | null) => {
    if (!tabKey) return

    setActiveTab(tabKey)

    // Load data for non-summary tabs if not already loaded
    if (tabKey !== 'summary' && !tabData[tabKey]) {
      loadTabData(tabKey)
    }
  }

  const handlePageChange = (tabKey: string, page: number) => {
    loadTabData(tabKey, page)
  }

  const renderPaginatedTable = (tabKey: string, tableClass: string) => {
    const currentTabData = tabData[tabKey]
    const isLoading = loadingTabs.has(tabKey)

    if (isLoading) {
      return (
        <div
          className='d-flex flex-column justify-content-center align-items-center'
          style={{height: '200px'}}
        >
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>
              {intl.formatMessage({id: 'CSV_IMPORT.COMMON.LOADING'})}
            </span>
          </div>
          <span className='text-muted fw-semibold fs-7 mt-3'>
            {intl.formatMessage({id: 'CSV_IMPORT.COMMON.LOADING'})}
          </span>
        </div>
      )
    }

    if (!currentTabData || currentTabData.data.length === 0) {
      return (
        <div
          className='d-flex flex-column align-items-center justify-content-center text-muted py-15'
          style={{minHeight: '200px'}}
        >
          <KTIcon iconName='folder' className='fs-3x text-gray-400 mb-3' />
          <span className='fw-semibold fs-6'>
            {intl.formatMessage({id: 'CSV_IMPORT.COMMON.NO_DATA_AVAILABLE'})}
          </span>
        </div>
      )
    }

    const {data, totalCount, currentPage} = currentTabData
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE)

    // Arrays and objects (e.g. subjects: [{name, class, ...}]) must not render as "[object Object]"
    const formatCellValue = (value: any): string => {
      if (value === undefined || value === null || value === '') return ' - '
      if (Array.isArray(value)) {
        const parts = value.map((item) => formatCellValue(item)).filter((s) => s !== ' - ')
        return parts.length > 0 ? parts.join(', ') : ' - '
      }
      if (typeof value === 'object') {
        const name = value.name ?? value.label ?? value.value
        return name !== undefined && name !== null && name !== ''
          ? String(name)
          : JSON.stringify(value)
      }
      return String(value)
    }

    // teacher_employee_id -> Teacher Employee ID
    const humanizeHeader = (key: string) =>
      key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim()
        .split(/\s+/)
        .map((w) => (w.toLowerCase() === 'id' ? 'ID' : w.charAt(0).toUpperCase() + w.slice(1)))
        .join(' ')

    const headers =
      tabKey === 'errors'
        ? [
            {key: 'row', label: 'Row', priority: 'high'},
            {key: 'error', label: 'Error', priority: 'high'},
            ...(data[0].data
              ? Object.keys(data[0].data).map((k, idx) => ({
                  key: k,
                  label: humanizeHeader(k),
                  priority: idx === 0 ? 'high' : 'low',
                }))
              : []),
          ]
        : Object.keys(data[0]).map((k, idx) => ({
            key: k,
            label: humanizeHeader(k),
            priority: idx < 2 ? 'high' : 'low',
          }))

    return (
      <div className='mb-4'>
        <div className='table-responsive' style={{maxHeight: '400px', overflowY: 'auto'}}>
          <Table className='table align-middle gs-0 gy-3 compact-table'>
            <thead className={tableClass}>
              <tr>
                {headers.map(({key, label, priority}) => (
                  <th
                    key={key}
                    data-priority={priority}
                    style={{fontSize: '0.8rem', padding: '0.5rem 0.75rem', whiteSpace: 'nowrap'}}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((record, idx) => (
                <tr key={idx}>
                  {headers.map(({key, label, priority}) => {
                    if (key === 'row') {
                      return (
                        <td
                          key={key}
                          data-label={label}
                          data-priority={priority}
                          style={{
                            fontSize: '0.75rem',
                            padding: '0.5rem 0.75rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {record.row ?? (currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                        </td>
                      )
                    }

                    if (key === 'error') {
                      return (
                        <td
                          key={key}
                          data-label={label}
                          style={{
                            fontSize: '0.75rem',
                            padding: '0.5rem 0.75rem',
                            fontWeight: 'bold',
                          }}
                        >
                          <i
                            className='bi bi-exclamation-circle-fill me-2'
                            style={{fontSize: '0.9rem'}}
                            aria-hidden='true'
                          ></i>
                          {record.error || ' - '}
                        </td>
                      )
                    }

                    const value = tabKey === 'errors' ? record.data?.[key] : record[key]
                    const cellValue = formatCellValue(value)

                    return (
                      <td
                        key={key}
                        data-label={label}
                        data-priority={priority}
                        style={{fontSize: '0.75rem', padding: '0.5rem 0.75rem'}}
                      >
                        {cellValue}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className='d-flex justify-content-between align-items-center mt-3'>
          <div className='flex-grow-1'>
            {totalPages > 1 && (
              <Pagination size='sm' className='mb-0 pagination pagination-sm'>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(tabKey, 1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(tabKey, currentPage - 1)}
                />

                {/* Show page numbers */}
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  const page = Math.max(1, currentPage - 2) + index
                  if (page <= totalPages) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(tabKey, page)}
                      >
                        {page}
                      </Pagination.Item>
                    )
                  }
                  return null
                })}

                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(tabKey, currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(tabKey, totalPages)}
                />
              </Pagination>
            )}
          </div>
          <div className='ms-3 text-muted fw-semibold fs-7'>
            {totalCount}{' '}
            {totalCount === 1
              ? intl.formatMessage({id: 'CSV_IMPORT.COMMON.TOTAL_RECORD'})
              : intl.formatMessage({id: 'CSV_IMPORT.COMMON.TOTAL_RECORDS'})}
          </div>
        </div>
      </div>
    )
  }

  // Helper to parse message for counts
  const parseCountsFromMessage = (message?: string) => {
    if (!message) return null
    const createdMatch = message.match(/created (\d+)/i)
    const updatedMatch = message.match(/updated (\d+)/i)
    const errorMatch = message.match(/error[s]? (\d+)/i)
    const created = createdMatch ? parseInt(createdMatch[1], 10) : null
    const updated = updatedMatch ? parseInt(updatedMatch[1], 10) : null
    // Try to find errors in message, else fallback to errors array
    let errors = null
    if (errorMatch) {
      errors = parseInt(errorMatch[1], 10)
    }
    return {created, updated, errors}
  }

  const messageCounts = parseCountsFromMessage((result as any)?.message)
  const createdCount = messageCounts?.created ?? getTotalCount(result.created || {})
  const updatedCount = messageCounts?.updated ?? getTotalCount(result.updated || {})
  // For errors, prefer message if present, else fallback to array
  const errorCount = messageCounts?.errors ?? removeDuplicateErrors(result.errors || []).length
  const credentials = normalizeCredentialsSummary(result.credentials)

  // Get all unique table names from created and updated records
  const createdTables = Object.keys(result.created || {}).filter(
    (tableName) => (result.created?.[tableName]?.length || 0) > 0
  )
  const updatedTables = Object.keys(result.updated || {}).filter(
    (tableName) => (result.updated?.[tableName]?.length || 0) > 0
  )

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size='xl'
      centered
      className='import-result-modal'
      contentClassName='border-0 shadow-sm'
      dialogClassName='modal-dialog-centered'
    >
      <Modal.Header closeButton={false} className='border-0 pt-6 pb-0'>
        <Modal.Title className='fw-bolder fs-3 text-gray-900'>
          {intl.formatMessage({id: 'CSV_IMPORT.RESULT.TITLE'})}
        </Modal.Title>
        <div
          className='btn btn-sm btn-icon btn-active-color-primary'
          onClick={handleClose}
          aria-label='Close'
        >
          <KTIcon iconName='cross' className='fs-1' />
        </div>
      </Modal.Header>
      <Modal.Body className='scroll-y pt-5' style={{maxHeight: '70vh'}}>
        <div className='d-flex align-items-center bg-light-success rounded px-5 py-4 mb-6'>
          <span className='symbol symbol-50px me-4'>
            <span className='symbol-label bg-light-success'>
              <KTIcon iconName='check-circle' className='fs-2x text-success' />
            </span>
          </span>
          <div>
            <span className='fw-bold fs-4 text-gray-900 d-block'>
              {intl.formatMessage({id: 'CSV_IMPORT.RESULT.IMPORT_COMPLETED'})}
            </span>
            <span className='text-muted fw-semibold fs-7'>
              {intl.formatMessage({id: 'CSV_IMPORT.RESULT.PROCESSED_DESCRIPTION'})}
            </span>
          </div>
        </div>

        {credentials && <ImportCredentials summary={credentials} />}

        <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
          <div className='vertical-layout'>
            <Nav variant='pills' className='vertical-tabs'>
              <Nav.Item>
                <Nav.Link eventKey='summary'>
                  {intl.formatMessage({id: 'CSV_IMPORT.RESULT.SUMMARY'})}
                </Nav.Link>
              </Nav.Item>
              {createdTables.map((tableName) => (
                <Nav.Item key={`created-${tableName}`}>
                  <Nav.Link eventKey={`created-${tableName}`} title={`${tableName} (Created)`}>
                    {tableName} (Created)
                  </Nav.Link>
                </Nav.Item>
              ))}
              {updatedTables.map((tableName) => (
                <Nav.Item key={`updated-${tableName}`}>
                  <Nav.Link eventKey={`updated-${tableName}`} title={`${tableName} (Updated)`}>
                    {tableName} (Updated)
                  </Nav.Link>
                </Nav.Item>
              ))}
              {errorCount > 0 && (
                <Nav.Item>
                  <Nav.Link eventKey='errors' className='errors-tab-link'>
                    <i className='bi bi-exclamation-circle-fill me-2 text-danger'></i>
                    {intl.formatMessage({id: 'CSV_IMPORT.RESULT.ERRORS'})}
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>

            <div className='tab-content-vertical'>
              <Tab.Content>
                <Tab.Pane eventKey='summary'>
                  <div className='row g-5'>
                    <div className='col-md-4'>
                      <div className='card card-flush h-100'>
                        <div className='card-body d-flex align-items-center justify-content-between p-5'>
                          <div className='d-flex flex-column'>
                            <span className='text-gray-500 fw-semibold fs-7 mb-1'>
                              {intl.formatMessage({id: 'CSV_IMPORT.RESULT.CREATED'})}
                            </span>
                            <span className='fw-bolder fs-2x text-success'>{createdCount}</span>
                            <span className='text-muted fw-semibold fs-8'>
                              {intl.formatMessage({id: 'CSV_IMPORT.RESULT.NEW_RECORDS_ADDED'})}
                            </span>
                          </div>
                          <span className='symbol symbol-50px'>
                            <span className='symbol-label bg-light-success'>
                              <KTIcon iconName='plus-square' className='fs-2x text-success' />
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='card card-flush h-100'>
                        <div className='card-body d-flex align-items-center justify-content-between p-5'>
                          <div className='d-flex flex-column'>
                            <span className='text-gray-500 fw-semibold fs-7 mb-1'>
                              {intl.formatMessage({id: 'CSV_IMPORT.RESULT.UPDATED'})}
                            </span>
                            <span className='fw-bolder fs-2x text-primary'>{updatedCount}</span>
                            <span className='text-muted fw-semibold fs-8'>
                              {intl.formatMessage({id: 'CSV_IMPORT.RESULT.RECORDS_MODIFIED'})}
                            </span>
                          </div>
                          <span className='symbol symbol-50px'>
                            <span className='symbol-label bg-light-primary'>
                              <KTIcon iconName='pencil' className='fs-2x text-primary' />
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='card card-flush h-100'>
                        <div className='card-body d-flex align-items-center justify-content-between p-5'>
                          <div className='d-flex flex-column'>
                            <span className='text-gray-500 fw-semibold fs-7 mb-1'>
                              {intl.formatMessage({id: 'CSV_IMPORT.RESULT.ERRORS'})}
                            </span>
                            <span className='fw-bolder fs-2x text-danger'>{errorCount}</span>
                            <span className='text-muted fw-semibold fs-8'>
                              {intl.formatMessage({id: 'CSV_IMPORT.RESULT.FAILED_RECORDS'})}
                            </span>
                          </div>
                          <span className='symbol symbol-50px'>
                            <span className='symbol-label bg-light-danger'>
                              <KTIcon iconName='cross-circle' className='fs-2x text-danger' />
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Tab.Pane>

                {activeTab === 'summary' &&
                  !loadingTimetableStats &&
                  timetableStats &&
                  (() => {
                    const filteredStatsConfig = isClasswise
                      ? STATS_CONFIG.filter(({key}) => key !== 'student_count')
                      : STATS_CONFIG
                    const emptyStats = filteredStatsConfig.filter(
                      ({key}) => (timetableStats[key] ?? 0) === 0
                    )
                    return emptyStats.length > 0 ? (
                      <div className='d-flex align-items-start bg-light-danger rounded px-5 py-4 mt-6'>
                        <span className='symbol symbol-50px me-4'>
                          <span className='symbol-label bg-light-danger'>
                            <KTIcon iconName='information-5' className='fs-2x text-danger' />
                          </span>
                        </span>
                        <div className='flex-grow-1'>
                          <span className='fw-bold fs-5 text-gray-900 d-block mb-2'>
                            {intl.formatMessage({id: 'CSV_IMPORT.RESULT.MISSING_REFERENCE_DATA'})}
                          </span>
                          <p className='text-gray-600 fw-semibold fs-7 mb-3'>
                            {intl.formatMessage({
                              id: 'CSV_IMPORT.RESULT.MISSING_REFERENCE_DESCRIPTION',
                            })}
                          </p>
                          <ul className='mb-0'>
                            {emptyStats.map(({key, labelId, linkTo}) => (
                              <li key={key} className='mb-2'>
                                <strong className='text-gray-700'>
                                  {intl.formatMessage({id: labelId})}
                                </strong>
                                {' — '}
                                <Link
                                  to={linkTo}
                                  state={{from: 'csv-import', backUrl: location.pathname}}
                                  className='text-primary fw-bold text-hover-primary'
                                >
                                  {intl.formatMessage(
                                    {id: 'CSV_IMPORT.RESULT.ADD_LABEL'},
                                    {label: intl.formatMessage({id: labelId}).toLowerCase()}
                                  )}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ) : null
                  })()}
                {createdTables.map((tableName) => (
                  <Tab.Pane key={`created-${tableName}`} eventKey={`created-${tableName}`}>
                    <h4 className='fw-bolder text-gray-900 mb-4'>
                      <KTIcon iconName='plus-square' className='fs-2 text-success me-2' />
                      {tableName} - Created Records
                    </h4>
                    {renderPaginatedTable(`created-${tableName}`, 'table-success')}
                  </Tab.Pane>
                ))}

                {updatedTables.map((tableName) => (
                  <Tab.Pane key={`updated-${tableName}`} eventKey={`updated-${tableName}`}>
                    <h4 className='fw-bolder text-gray-900 mb-4'>
                      <KTIcon iconName='pencil' className='fs-2 text-primary me-2' />
                      {tableName} - Updated Records
                    </h4>
                    {renderPaginatedTable(`updated-${tableName}`, 'table-info')}
                  </Tab.Pane>
                ))}

                {errorCount > 0 && (
                  <Tab.Pane eventKey='errors'>
                    <h4 className='fw-bolder text-gray-900 mb-4'>
                      <KTIcon iconName='cross-circle' className='fs-2 text-danger me-2' />
                      {intl.formatMessage({id: 'CSV_IMPORT.RESULT.ERROR_DETAILS'})}
                    </h4>
                    {renderPaginatedTable('errors', 'table-danger')}
                  </Tab.Pane>
                )}
              </Tab.Content>
            </div>
          </div>
        </Tab.Container>
      </Modal.Body>
      <Modal.Footer className='border-0 pt-0'>
        <Button onClick={handleClose} className='btn btn-light me-3'>
          {intl.formatMessage({id: 'CSV_IMPORT.RESULT.IMPORT_MORE'})}
        </Button>
        <Button onClick={handleDone} className='btn btn-primary'>
          {intl.formatMessage({id: 'GENERAL.DONE'})}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ImportResultModal
